import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type SubscriptionPlanInput } from '../api.js';
import { useActiveProfileId } from '../active-profile.js';
import { CopyCodeBlock, Switch } from '../components/ui.js';
import { Topbar } from './AdminLayout.js';
import { BRAND } from '../branding.js';

const RU_TO_EN: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};

function slugify(input: string): string {
  return Array.from(input.toLowerCase().trim())
    .map((ch) => (ch in RU_TO_EN ? RU_TO_EN[ch] : ch))
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

const APP_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : 'https://pay.banditodigital.com';
const DEFAULT_SUCCESS_URL = `${APP_ORIGIN}/paid`;
const DEFAULT_FAIL_URL = `${APP_ORIGIN}/fail`;

const empty: SubscriptionPlanInput = {
  slug: '',
  name: '',
  description: '',
  priceIls: 0,
  merchant: '',
  successUrl: DEFAULT_SUCCESS_URL,
  failUrl: DEFAULT_FAIL_URL,
  isActive: true,
};

export function SubscriptionPlanEdit(): JSX.Element {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const nav = useNavigate();
  const [form, setForm] = useState<SubscriptionPlanInput>(empty);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [shortUrl, setShortUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [activeProfileId] = useActiveProfileId();
  const [initialProfileId] = useState(activeProfileId);
  useEffect(() => {
    if (initialProfileId !== null && activeProfileId !== initialProfileId) {
      nav('/admin/plans', { replace: true });
    }
  }, [activeProfileId, initialProfileId, nav]);

  useEffect(() => {
    if (isNew) return;
    api
      .getPlan(id!)
      .then((p) => {
        setForm({
          slug: p.slug,
          name: p.name,
          description: p.description,
          priceIls: p.priceIls,
          merchant: p.merchant ?? '',
          successUrl: p.successUrl,
          failUrl: p.failUrl,
          isActive: p.isActive,
        });
        setShortUrl(p.shortUrl);
        setLoading(false);
      })
      .catch((e: Error) => {
        setErr(e.message);
        setLoading(false);
      });
  }, [id, isNew]);

  const update = <K extends keyof SubscriptionPlanInput>(
    k: K,
    v: SubscriptionPlanInput[K],
  ): void => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (isNew) {
        const created = await api.createPlan(form);
        nav(`/admin/plans/${created.id}`, { replace: true });
      } else {
        await api.updatePlan(id!, form);
        nav('/admin/plans');
      }
    } catch (e) {
      const d = (e as { details?: unknown }).details;
      setErr(typeof d === 'string' ? d : JSON.stringify(d));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    if (!confirm('Удалить план? (только если на нём нет активных подписок)')) return;
    try {
      await api.deletePlan(id!);
      nav('/admin/plans');
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--fg-subtle)' }}>Загрузка…</div>;

  const standaloneUrl = `${APP_ORIGIN}/s/${form.slug || '<slug>'}`;

  return (
    <>
      <Topbar
        title={isNew ? 'Новый план' : <>План: <bdi>{form.name || form.slug}</bdi></>}
        actions={
          <>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => nav('/admin/plans')}
            >
              Отмена
            </button>
            <button
              type="submit"
              form="plan-form"
              className="btn btn-primary btn-sm"
              disabled={busy || !form.name.trim() || !form.slug.trim() || form.priceIls <= 0}
            >
              {isNew ? 'Создать' : 'Сохранить'}
            </button>
          </>
        }
      />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        <form id="plan-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 14, maxWidth: 720 }}>
          {err && (
            <div
              className="card"
              style={{
                padding: 14,
                color: 'var(--danger-fg)',
                background: 'var(--danger-subtle-bg)',
              }}
            >
              {err}
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <div className="card-title">Основное</div>
            </div>
            <div className="card-body stack">
              <div className="grid-2">
                <div className="field">
                  <label className="field-label">Название *</label>
                  <input
                    className="input"
                    value={form.name}
                    onChange={(e) => {
                      update('name', e.target.value);
                      if (!slugTouched) update('slug', slugify(e.target.value));
                    }}
                  />
                </div>
                <div className="field">
                  <label className="field-label">URL slug *</label>
                  <input
                    className="input mono"
                    value={form.slug}
                    onChange={(e) => {
                      update('slug', e.target.value);
                      setSlugTouched(true);
                    }}
                    placeholder="my-plan"
                  />
                  <div style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 4 }}>
                    /s/{form.slug || '<slug>'}
                  </div>
                </div>
              </div>
              <div className="field">
                <label className="field-label">Описание</label>
                <textarea
                  className="input"
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  placeholder="Что входит в подписку"
                  rows={3}
                />
              </div>
              <div className="grid-2">
                <div className="field">
                  <label className="field-label">Сумма ежемесячно (ILS) *</label>
                  <input
                    className="input mono"
                    type="number"
                    min={1}
                    step={1}
                    value={form.priceIls / 100}
                    onChange={(e) => update('priceIls', Math.round(+e.target.value * 100))}
                  />
                </div>
                <div className="field">
                  <label className="field-label">Merchant (бренд)</label>
                  <input
                    className="input"
                    value={form.merchant}
                    onChange={(e) => update('merchant', e.target.value)}
                    placeholder={BRAND.name}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Публикация</div>
            </div>
            <div className="card-body stack">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>Активен</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                    Принимает новые подписки
                  </div>
                </div>
                <Switch
                  checked={form.isActive}
                  onChange={(v) => update('isActive', v)}
                />
              </div>
              <div className="field">
                <label className="field-label">Standalone-страница</label>
                <CopyCodeBlock text={standaloneUrl} />
              </div>
              {shortUrl && (
                <div className="field">
                  <label className="field-label">Короткая ссылка</label>
                  <CopyCodeBlock text={shortUrl} />
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Редиректы</div>
            </div>
            <div className="card-body stack">
              <div className="field">
                <label className="field-label">Success URL *</label>
                <input
                  className="input mono"
                  type="url"
                  value={form.successUrl}
                  onChange={(e) => update('successUrl', e.target.value)}
                />
              </div>
              <div className="field">
                <label className="field-label">Fail URL *</label>
                <input
                  className="input mono"
                  type="url"
                  value={form.failUrl}
                  onChange={(e) => update('failUrl', e.target.value)}
                />
              </div>
            </div>
          </div>

          {!isNew && (
            <div className="card">
              <div className="card-header">
                <div className="card-title">Действия</div>
              </div>
              <div className="card-body stack">
                <button
                  type="button"
                  className="btn btn-sm btn-ghost btn-danger"
                  onClick={() => void onDelete()}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Удалить план
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </>
  );
}
